/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2021 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadQuotes from "@insite/client-framework/Store/Data/Quotes/Handlers/LoadQuotes";
import { getQuotesDataView } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import CardContainer, { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import CardList, { CardListStyles } from "@insite/content-library/Components/CardList";
import CardListHeading from "@insite/content-library/Components/CardListHeading";
import QuoteSummaryCard, { QuoteSummaryCardStyles } from "@insite/content-library/Components/QuoteSummaryCard";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import Notify from "@monsma/XDL/Components/Notify";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    return {
        settingsCollection: getSettingsCollection(state),
        session,
        quotesDataView: getQuotesDataView(state, recentQuotesParameter),
        myQuotesPageNavLink: getPageLinkByPageType(state, "RfqMyQuotesPage"),
        canViewQuotes: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
    };
};

const mapDispatchToProps = {
    loadQuotes,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

// xng-custom XStyle injection, controlled from extentions
export interface XStyles {
    headerStyling?: InjectableCss;
    tableGridItem?: GridItemProps;
    table?: DataTableProps;
    quoteNumberHeader?: DataTableHeaderProps;
    typeHeader?: DataTableHeaderProps;
    statusHeader?: DataTableHeaderProps;
    orderDateHeader?: DataTableHeaderProps;
    expirationDateHeader?: DataTableHeaderProps;
    salesRepHeader?: DataTableHeaderProps;
    userHeader?: DataTableHeaderProps;
    customerHeader?: DataTableHeaderProps;
    shipToHeader?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableHead?: DataTableHeadProps;
}

export interface RecentQuotesStyles {
    cardList?: GridContainerProps;
    noQuotesGridItem?: GridItemProps;
    noQuotesText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    quoteSummaryCard?: QuoteSummaryCardStyles;

    // custom
    xRecentQuotesStyles?: XStyles;
}

export const recentQuotesStyles: RecentQuotesStyles = {
    noQuotesGridItem: { width: 12 },
};

const recentQuotesParameter = {
    page: 1,
    pageSize: 5,
};

const styles = recentQuotesStyles;

class RecentQuotes extends React.Component<Props> {
    componentDidMount() {
        if (this.props.canViewQuotes && !this.props.quotesDataView.value) {
            this.props.loadQuotes(recentQuotesParameter);
        }
    }

    render() {
        const { settingsCollection, session, quotesDataView, myQuotesPageNavLink, canViewQuotes } = this.props;
        if (!canViewQuotes || !quotesDataView.value) {
            return null;
        }

        const myQuotesPageUrl = myQuotesPageNavLink ? myQuotesPageNavLink.url : undefined;

        // xng-change: restructuring the content to be table view
        return (
            <CardList {...styles.cardList}>
                <StyledWrapper {...styles.xRecentQuotesStyles?.headerStyling}>
                    <Typography variant="h4">{translate("Recent Quotes")}</Typography>
                    <Link href={myQuotesPageUrl}>{translate("View All quotes")}</Link>
                </StyledWrapper>
                {quotesDataView.value.length === 0 && (
                    <GridItem {...styles.noQuotesGridItem}>
                        <Notify messageType="danger" body={siteMessage("Rfq_NoQuotesMessage")} />
                    </GridItem>
                )}
                {quotesDataView && quotesDataView.value && quotesDataView.value.length > 0 && (
                    <GridItem {...styles.xRecentQuotesStyles?.tableGridItem}>
                        <DataTable {...styles.xRecentQuotesStyles?.table}>
                            <DataTableHead {...styles.xRecentQuotesStyles?.dataTableHead}>
                                <DataTableHeader tight {...styles.xRecentQuotesStyles?.quoteNumberHeader}>
                                    {translate("Quote #")}
                                </DataTableHeader>
                                <DataTableHeader tight {...styles.xRecentQuotesStyles?.orderDateHeader}>
                                    {translate("Requested")}
                                </DataTableHeader>
                                <DataTableHeader tight {...styles.xRecentQuotesStyles?.expirationDateHeader}>
                                    {translate("Expires")}
                                </DataTableHeader>
                                {session.isSalesPerson && (
                                    <>
                                        <DataTableHeader tight {...styles.xRecentQuotesStyles?.salesRepHeader}>
                                            {translate("Sales Rep")}
                                        </DataTableHeader>
                                        <DataTableHeader tight {...styles.xRecentQuotesStyles?.userHeader}>
                                            {translate("User")}
                                        </DataTableHeader>
                                    </>
                                )}
                                <DataTableHeader tight {...styles.xRecentQuotesStyles?.customerHeader}>
                                    {translate("Customer")}
                                </DataTableHeader>
                                {!session.isSalesPerson && (
                                    <DataTableHeader tight {...styles.xRecentQuotesStyles?.shipToHeader}>
                                        {translate("Ship To / Pick Up")}
                                    </DataTableHeader>
                                )}
                            </DataTableHead>
                            <DataTableBody {...styles.xRecentQuotesStyles?.dataTableBody}>
                                {quotesDataView.value.map(quote => (
                                    <QuoteSummaryCard
                                        quote={quote}
                                        session={session}
                                        key={quote.id}
                                        quoteSettings={settingsCollection.quoteSettings}
                                        extendedStyles={styles.quoteSummaryCard}
                                    />
                                ))}
                            </DataTableBody>
                        </DataTable>
                    </GridItem>
                )}
            </CardList>
        );
    }
}

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentQuotes),
    definition: {
        group: "Common",
        icon: "list-ul",
    },
};

export default widgetModule;
